import { Link } from "react-router-dom";
import { ArrowUpRight, Check, X, Database, Filter, Scale, Crosshair, TrendingUp, CloudFog, Percent, Puzzle, Layers, PiggyBank, Wallet, Receipt, Stethoscope, Video } from "lucide-react";
import { MaskedLines, Reveal, ChapterNumber } from "../components/motion";
import { PEOForm } from "../components/PEOForm";
import { IMAGES, services } from "../data/content";
import { usePageTitle } from "../lib/title";

const leaks = [
  {
    icon: CloudFog,
    title: "Bundled Pricing Fog",
    body: "One invoice, no line items. If you can't see what each piece costs, it's hard to know what you're paying for.",
  },
  {
    icon: Percent,
    title: "Annual Repricing Creep",
    body: "Renewals arrive with increases nobody benchmarks. Compounded quietly, year after year, they become the real cost.",
  },
  {
    icon: Puzzle,
    title: "Services You Never Use",
    body: "HR modules, training libraries, and tools your team never touches — all quietly baked into the admin fee.",
  },
  {
    icon: Layers,
    title: "One-Size-Fits-All Pricing",
    body: "Your company isn't average. Your workforce, risk profile, and industry shouldn't be priced like they are.",
  },
];

const modernOptions = [
  {
    icon: PiggyBank,
    title: "Level-Funded Plans",
    what: "You pay a steady monthly amount that covers claims, admin, and stop-loss protection — and, depending on the plan’s terms, part of any unused claims fund may be refunded or credited after the plan year.",
    why: "You get regular claims reporting, so you can see how your plan is actually being used.",
  },
  {
    icon: Wallet,
    title: "CHOICE Arrangement (formerly ICHRA)",
    what: "Instead of buying one group plan, you as the employer set a monthly allowance and reimburse employees tax-free for the individual plan each one chooses.",
    why: "Your cost is a number you choose rather than a renewal you receive, and people keep their own doctors and plan.",
  },
  {
    icon: Receipt,
    title: "Reference-Based Pricing",
    what: "The plan pays providers a set price tied to published benchmark rates for the same service.",
    why: "Prices are set and visible up front. It takes strong member support, and it isn't the right fit for every team.",
  },
  {
    icon: Stethoscope,
    title: "Direct Primary Care",
    what: "A flat monthly membership with a local primary care practice — longer visits, same-day access, texting your doctor, and no copay at the door.",
    why: "It can give your team easier everyday access to a doctor, and it pairs with a health plan rather than replacing it.",
  },
  {
    icon: Video,
    title: "Virtual-First Care",
    what: "A plan built around a virtual doctor as the front door, with in-person care and specialists referred from there.",
    why: "It may fit a spread-out or shift-based workforce that values care by phone or video.",
  },
];

const peoSteps = [
  {
    icon: Database,
    title: "Gather the Full Picture",
    body: "Your census, current PEO costs, claims patterns, service usage, and HR workload — the raw material every honest comparison needs.",
  },
  {
    icon: Filter,
    title: "High-Resolution Filtering",
    body: "I filter the PEO market at high resolution — pricing models, benefit pools, service track records, industry fit — cutting dozens of options to a credible few.",
  },
  {
    icon: Scale,
    title: "True Like-for-Like Comparison",
    body: "Every finalist is normalized against your current PEO and each other, so total cost and value compare apples to apples — no bundled-price fog.",
  },
  {
    icon: Crosshair,
    title: "Personal Side-by-Side Review",
    body: "I personally compare PEO options against your company's size, industry, and workforce profile — and show you how I got to each recommendation.",
  },
  {
    icon: TrendingUp,
    title: "Only Upside",
    body: "A move only makes sense when the data shows clear, durable value. If staying put wins, I'll tell you to stay — that's the whole point of an honest analysis.",
  },
];

const peoFits = [
  "You've never benchmarked your PEO against the market",
  "Renewals arrive with increases you can't verify",
  "You're paying for HR services your team rarely uses",
  "You've grown — and your PEO pricing hasn't noticed",
];

const peoDoesntFit = [
  "Your PEO already benchmarks at or below market",
  "Your team genuinely uses the full service stack",
  "Your pricing reflects your current size and profile",
];

export default function Employers() {
  usePageTitle("Employee Benefits Guidance for Employers — The Woodlands, TX");
  return (
    <div data-testid="employers-page">
      {/* HERO */}
      <section className="mx-auto grid max-w-7xl items-center gap-12 px-6 pb-20 pt-36 lg:grid-cols-12 lg:px-10 lg:pt-44" data-testid="employers-hero">
        <div className="lg:col-span-7">
          <h1 className="font-mono text-xs uppercase tracking-[0.35em] text-terra" data-testid="employers-kicker">
            Group Health Insurance &amp; PEO Broker · The Woodlands &amp; Houston
          </h1>
          <MaskedLines
            as="p"
            className="mt-8 font-serif text-5xl font-light leading-[0.95] tracking-tighter text-forest sm:text-6xl lg:text-7xl"
            lines={[
              "Every benefits",
              <span key="l2">
                <em className="text-terra">question, answered.</em>
              </span>,
            ]}
          />
          <Reveal delay={0.4}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-ink/70 sm:text-lg">
              Group health, ancillary coverage, funding strategy, renewals, enrollment education — if it
              touches your team&rsquo;s benefits, start the conversation here. You don&rsquo;t need a PEO to work
              with me; most employers I help don&rsquo;t have one. And if you do, I can give you an honest,
              data-driven read on how it compares.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact?type=consultation"
                data-testid="employers-hero-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-forest px-8 py-3.5 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-1 hover:bg-moss"
              >
                Request a Consultation
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a
                href="#peo"
                data-testid="employers-peo-cta"
                className="group inline-flex items-center gap-2 rounded-full border border-forest/30 px-8 py-3.5 text-sm font-medium text-forest transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:text-terra"
              >
                Group Health &amp; PEO Options
              </a>
            </div>
            <Link
              to="/group-benefits-the-woodlands"
              data-testid="employers-woodlands-link"
              className="group mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-terra transition-colors duration-300 hover:text-forest"
            >
              Based near The Woodlands? See how I help local employers with group health plans
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
        <Reveal className="lg:col-span-4 lg:col-start-9" delay={0.2}>
          <div className="clipped-frame overflow-hidden">
            <img
              src={IMAGES.team}
              alt="Professional team collaborating in a warm office"
              className="h-[380px] w-full object-cover transition-transform duration-700 hover:scale-105 lg:h-[480px]"
              data-testid="employers-hero-image"
            />
          </div>
        </Reveal>
      </section>

      {/* CAPABILITIES */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28" data-testid="employers-capabilities">
        <Reveal>
          <ChapterNumber n="01" />
          <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
            Any benefits need. <em className="text-terra">One trusted table.</em>
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/70">
            Health plans, ancillary coverage, funding strategy, employee education, renewal defense — bring me
            whatever is in front of you. There&rsquo;s no minimum, no PEO prerequisite, and no wrong question to
            start with.
          </p>
        </Reveal>
        <div className="mt-12">
          {services.employers.items.map((item, i) => (
            <Reveal key={item.title} delay={0.04 * i}>
              <div
                className={`flex gap-4 border-t border-border py-6 ${i === services.employers.items.length - 1 ? "border-b" : ""}`}
                data-testid={`employers-capability-${i}`}
              >
                <Check className="mt-1 h-4 w-4 shrink-0 text-gold" strokeWidth={2.5} />
                <div>
                  <h3 className="font-serif text-xl font-medium tracking-tight text-forest sm:text-2xl">{item.title}</h3>
                  <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-ink/65">{item.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BEYOND THE TRADITIONAL GROUP PLAN */}
      <section className="border-t border-border" data-testid="employers-modern-options">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-terra" data-testid="modern-options-kicker">
              Beyond the Traditional Group Plan
            </p>
            <h2 className="mt-5 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
              There are more ways to fund care <em className="text-terra">than most employers are shown</em>
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/70">
              A fully insured plan with a big network is one answer, not the only one. These models are available
              today and often skipped in a standard renewal conversation. Not every one of them will
              fit your team — but you should get to decide that with the options in front of you.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {modernOptions.map((option, i) => (
              <Reveal key={option.title} delay={0.06 * i}>
                <div
                  className="h-full border border-border bg-cream p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold/60"
                  data-testid={`employers-modern-option-${i}`}
                >
                  <option.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                  <h3 className="mt-6 font-serif text-xl font-medium tracking-tight text-forest">{option.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/65">{option.what}</p>
                  <p className="mt-3 text-sm leading-relaxed text-ink/55">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra">Why consider it · </span>
                    {option.why}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHERE VALUE LEAKS */}
      <section className="bg-sand/70" data-testid="peo-leaks">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <ChapterNumber n="02" />
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.35em] text-terra" data-testid="peo-specialty-kicker">
              A Specialty, Not a Requirement
            </p>
            <h2 className="mt-5 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
              If you&rsquo;re on a PEO, here&rsquo;s where value <em className="text-terra">quietly leaks</em>
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/70">
              The next two sections are for employers who already use a PEO or are weighing one. Not on a PEO?
              Skip ahead — everything above still applies, and I&rsquo;d be glad to help either way.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {leaks.map((l, i) => (
              <Reveal key={l.title} delay={0.08 * i}>
                <div
                  className="h-full border border-border bg-cream p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold/60"
                  data-testid={`peo-leak-${i}`}
                >
                  <l.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                  <h3 className="mt-6 font-serif text-xl font-medium tracking-tight text-forest">{l.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/65">{l.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PEO PROCESS */}
      <section id="peo" className="relative overflow-hidden bg-forest" data-testid="peo-section">
        <img src={IMAGES.texture} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-10" />
        <div className="relative mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-gold" data-testid="peo-kicker">
              PEO Education · My Analysis Process
            </p>
            <h2 className="mt-6 max-w-4xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl">
              Vetting PEOs? <em className="text-gold">Let the data decide.</em>
            </h2>
            <div className="mt-8 max-w-3xl space-y-5 text-base leading-relaxed text-cream/75">
              <p>
                As an independent agent, I personally compare PEO options for your business against your current costs, services and workforce, so if you make a move, it&rsquo;s because the numbers support it, not because someone sold it.
              </p>
              <p>
                And if the analysis says your current PEO is already the right partner at the right price?
                You&rsquo;ll hear that too — in writing, with the benchmarks to prove it. Never been on a PEO? The
                same process tells you honestly whether one belongs in your plan at all.
              </p>
            </div>
          </Reveal>

          <div className="mt-16 grid gap-px overflow-hidden border border-cream/15 bg-cream/15 sm:grid-cols-2 lg:grid-cols-5" data-testid="peo-process">
            {peoSteps.map((s, i) => (
              <Reveal key={s.title} delay={0.06 * i} className="h-full">
                <div className="flex h-full flex-col bg-forest p-7" data-testid={`peo-step-${i + 1}`}>
                  <s.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                  <span className="mt-6 font-mono text-[10px] tracking-[0.35em] text-gold/70">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-3 font-serif text-xl font-medium leading-snug tracking-tight text-cream">{s.title}</h3>
                  <p className="mt-3 text-xs leading-relaxed text-cream/65">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div
              className="mt-12 border border-gold/50 bg-gold/10 p-8 lg:p-10"
              data-testid="peo-only-upside"
            >
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-gold">The Only-Upside Promise</p>
              <p className="mt-4 max-w-3xl font-serif text-2xl font-light leading-snug text-cream sm:text-3xl">
                If a different PEO doesn&rsquo;t clearly beat your current position, I&rsquo;ll tell you to stay put.
                The process exists to find durable value — <em className="text-gold">nothing moves unless the data wins</em>.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHEN TO REVIEW */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="peo-fit-section">
        <Reveal>
          <ChapterNumber n="03" />
          <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
            Signs a review is overdue — <em className="text-terra">and signs you&rsquo;re set</em>
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          <Reveal>
            <div className="h-full border border-border p-8 lg:p-10" data-testid="peo-fits-card">
              <h3 className="font-serif text-2xl font-medium tracking-tight text-forest">Worth a conversation</h3>
              <ul className="mt-6 space-y-4">
                {peoFits.map((f, i) => (
                  <li key={i} className="flex gap-3 text-sm leading-relaxed text-ink/70" data-testid={`peo-fit-${i}`}>
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={2.5} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full border border-border bg-sand/50 p-8 lg:p-10" data-testid="peo-doesnt-fit-card">
              <h3 className="font-serif text-2xl font-medium tracking-tight text-forest">You're probably well-positioned</h3>
              <ul className="mt-6 space-y-4">
                {peoDoesntFit.map((f, i) => (
                  <li key={i} className="flex gap-3 text-sm leading-relaxed text-ink/70" data-testid={`peo-nofit-${i}`}>
                    <X className="mt-0.5 h-4 w-4 shrink-0 text-terra" strokeWidth={2.5} />
                    {f}
                  </li>
                ))}
              </ul>
              <p className="mt-6 border-t border-border pt-5 text-sm leading-relaxed text-ink/60">
                Either way, you leave the analysis knowing exactly where you stand — that clarity costs you
                nothing (carriers pay my commission), and it&rsquo;s yours to keep.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* EMPLOYER CONSULTATION */}
      <section className="mx-auto max-w-7xl px-6 pb-28 lg:px-10" data-testid="employers-form-section">
        <Reveal>
          <ChapterNumber n="04" />
        </Reveal>
        <Reveal delay={0.1}>
          <PEOForm />
        </Reveal>
      </section>
    </div>
  );
}
